/**
 * English: Hook that synchronizes chat transcript with Redis and WebSocket stream updates.
 * Thai: Hook ซิงค์ประวัติแชทกับ Redis และอัปเดตจาก WebSocket
 *
 * Extracted from ChatTestPage — contains 4 coherent side-effects:
 * 1. Initial Redis transcript load
 * 2. Post-task-complete Redis re-sync
 * 3. WebSocket message accumulation with clientOrder
 * 4. Timeout/error status side-effects
 */
import { useEffect, useRef, useState } from 'react';
import type { Dispatch, SetStateAction, MutableRefObject } from 'react';
import { ChatHistoryMessage, UserMessage } from '../components/chat-test/ChatHistory';
import { ChatInputMode } from '../components/chat-test/ChatInput';
import { SecondaryTab } from '../components/chat-test/ChatTabs';
import { ChatMessage, ChatHistoryRedisRow } from '../types';
import { loadChatTestTranscript } from '../utils/chatTestTranscriptCache';
import { compareChatDisplayOrder } from '../utils/chatDisplayOrder';
import { parseChatTimestampMs } from '../utils/chatTimestamp';

interface UseChatSyncParams {
  shouldSyncRedisTranscript: boolean;
  inputMode: ChatInputMode;
  secondaryTab: SecondaryTab | null;
  token: string | null;
  chatSessionScopeKey: string;
  chatSessionIdRef: MutableRefObject<string>;
  wsStatus: string;
  wsMessages: ChatMessage[];
  wsHookConversationId?: string;
  activeTaskId: string;
  loadChatHistory: (id: string) => Promise<{ messages: ChatHistoryRedisRow[] }>;
  setHistory: Dispatch<SetStateAction<ChatHistoryMessage[]>>;
  setUserMessages: Dispatch<SetStateAction<UserMessage[]>>;
  prevLenRef: MutableRefObject<number>;
  nextClientOrderRef: MutableRefObject<number>;
  lastSyncedChatTaskIdRef: MutableRefObject<string>;
}

export function useChatSync(p: UseChatSyncParams): { historyLoadError: string | null } {
  const [historyLoadError, setHistoryLoadError] = useState<string | null>(null);
  const {
    shouldSyncRedisTranscript,
    inputMode,
    secondaryTab,
    token,
    chatSessionScopeKey,
    chatSessionIdRef,
    wsStatus,
    wsMessages,
    wsHookConversationId,
    activeTaskId,
    loadChatHistory,
    setHistory,
    setUserMessages,
    prevLenRef,
    nextClientOrderRef,
    lastSyncedChatTaskIdRef,
  } = p;

  /** Scope when user opened a secondary tab — skip Redis full replace when returning to main with same scope. */
  const scopeWhenEnteredSecondaryRef = useRef<string | null>(null);
  const prevSecondaryTabRef = useRef<SecondaryTab | null>(null);

  // ── 1. Initial Redis transcript load ──
  useEffect(() => {
    const prevTab = prevSecondaryTabRef.current;
    const enteringSecondary = prevTab === null && secondaryTab !== null;
    const leavingSecondary = prevTab !== null && secondaryTab === null;

    if (enteringSecondary) {
      scopeWhenEnteredSecondaryRef.current = chatSessionScopeKey;
    }
    prevSecondaryTabRef.current = secondaryTab;

    if (!shouldSyncRedisTranscript) {
      setHistoryLoadError(null);
      return;
    }

    // Returning from Team Discussion / Master secondary views: in-memory transcript is source of truth;
    // reloading Redis here often replays an older snapshot and drops the latest user + AI bubbles.
    let skipFullRedisHydrate = false;
    if (leavingSecondary) {
      skipFullRedisHydrate =
        scopeWhenEnteredSecondaryRef.current !== null &&
        chatSessionScopeKey === scopeWhenEnteredSecondaryRef.current;
      scopeWhenEnteredSecondaryRef.current = null;
    }
    if (skipFullRedisHydrate) {
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        setHistoryLoadError(null);
        const { messages } = await loadChatHistory(chatSessionIdRef.current);
        if (cancelled) return;

        // Compare Redis snapshot vs session cache. Prefer cache when it is strictly newer
        // (e.g., user navigated mid-stream — backend hasn't persisted the latest turn yet, so
        // Redis returns a stale snapshot that would otherwise wipe the in-flight bubbles).
        const convId = chatSessionIdRef.current;
        const cached = loadChatTestTranscript(chatSessionScopeKey);
        const cachedUsable = cached && cached.conversationId === convId;
        const cachedTotal = cachedUsable ? cached.history.length + cached.userMessages.length : -1;

        if (messages.length === 0) {
          if (cachedTotal > 0) {
            setUserMessages(cached!.userMessages);
            setHistory(cached!.history);
            nextClientOrderRef.current = cached!.nextOrder;
            prevLenRef.current = 0;
          }
          return;
        }

        const { userMessages: um, history: hm, nextOrder } = buildChatStateFromRedisRows(messages);
        const redisTotal = um.length + hm.length;

        if (
          cachedUsable &&
          (cachedTotal > redisTotal ||
            latestCachedTranscriptMs(cached) > latestRedisTranscriptMs(um, hm))
        ) {
          setUserMessages(cached!.userMessages);
          setHistory(cached!.history);
          nextClientOrderRef.current = cached!.nextOrder;
          prevLenRef.current = 0;
          return;
        }

        // merge instead of replace to prevent wiping in-flight WS bubbles
        setHistory((prevLocal) => mergeChatHistory(prevLocal, hm));
        setUserMessages((prevLocal) => mergeUserMessages(prevLocal, um));
        nextClientOrderRef.current = Math.max(nextOrder, nextClientOrderRef.current);
        prevLenRef.current = 0;
      } catch (e) {
        if (!cancelled) {
          setHistoryLoadError((e as Error).message);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  // Stable refs/setters are intentionally excluded; scope key drives session changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSyncRedisTranscript, inputMode, secondaryTab, loadChatHistory, token, chatSessionScopeKey]);

  // ── 2. Post-WebSocket-complete Redis re-sync ──
  useEffect(() => {
    if (!shouldSyncRedisTranscript) return;
    if (wsStatus !== 'completed' && wsStatus !== 'stopped') return;
    if (!activeTaskId) return;
    if (lastSyncedChatTaskIdRef.current === activeTaskId) return;
    lastSyncedChatTaskIdRef.current = activeTaskId;

    let cancelled = false;
    void (async () => {
      try {
        const { messages } = await loadChatHistory(chatSessionIdRef.current);
        if (cancelled) return;
        // Redis can lag the WS "completed" frame — do not wipe in-memory transcript with [].
        if (messages.length === 0) return;
        // merge instead of replace so local user messages (typed while waiting) survive.
        const { userMessages: um, history: hm, nextOrder } = buildChatStateFromRedisRows(messages);
        // merge history instead of replace so local WS messages survive Redis lag
        setHistory((prevLocal) => mergeChatHistory(prevLocal, hm));
        prevLenRef.current = 0;
        setUserMessages((prevLocal) => mergeUserMessages(prevLocal, um));
        nextClientOrderRef.current = Math.max(nextOrder, nextClientOrderRef.current);
      } catch (e) {
        if (!cancelled) setHistoryLoadError((e as Error).message);
      }
    })();

    return () => {
      cancelled = true;
    };
  // Stable refs/setters are intentionally excluded; activeTaskId gates this re-sync.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSyncRedisTranscript, wsStatus, activeTaskId, loadChatHistory, chatSessionScopeKey]);

  // ── 3. WebSocket message accumulation (clientOrder preserves send/receive order vs server timestamps) ──
  useEffect(() => {
    // Guard: ignore WS messages if they belong to a different conversation scope (e.g. user switched tabs)
    if (wsHookConversationId && wsHookConversationId !== chatSessionIdRef.current) {
      return;
    }
    if (wsMessages.length < prevLenRef.current) {
      // New task started and cleared the hook's message buffer
      prevLenRef.current = 0;
    }
    if (wsMessages.length > prevLenRef.current) {
      const newMsgs = wsMessages.slice(prevLenRef.current);
      setHistory((prev) => {
        const seen = new Set(prev.map(_historyKey));
        const toAppend: ChatHistoryMessage[] = [];
        for (const msg of newMsgs) {
          const k = _historyKey(msg as ChatHistoryMessage);
          if (seen.has(k)) continue;
          seen.add(k);
          toAppend.push({
            ...msg,
            clientOrder: ++nextClientOrderRef.current,
          } as ChatHistoryMessage);
        }
        return toAppend.length ? [...prev, ...toAppend] : prev;
      });
      prevLenRef.current = wsMessages.length;
    }
  // Stable refs/setter are intentionally excluded; wsMessages is the event source.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsMessages, wsHookConversationId]);

  // ── 4. Timeout/error status side-effects ──
  useEffect(() => {
    if (
      wsStatus === 'completed' ||
      wsStatus === 'stopped' ||
      wsStatus === 'error' ||
      wsStatus === 'timeout'
    ) {
      // Do not reset to 0 — otherwise the next turn will re-append all accumulated messages.
      prevLenRef.current = wsMessages.length;
    }
    if (wsStatus === 'timeout') {
      setHistory((prev) => [
        ...prev,
        {
          agent_id: 'system',
          agent_name: 'System',
          content: 'หมดเวลารอการตอบสนอง — กรุณาส่งคำสั่งใหม่อีกครั้ง',
          timestamp: new Date().toISOString(),
          message_type: 'status',
          clientOrder: ++nextClientOrderRef.current,
        } as ChatHistoryMessage,
      ]);
    }
  // Stable refs/setter are intentionally excluded; only status changes trigger side effects.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsStatus]);

  return { historyLoadError };
}

// Duplicate of ChatTestPage helper — avoids circular dependency.
function redisRowModeToUserMode(m: string | undefined): string {
  if (m === 'ask' || m === 'insight' || m === 'insights' || m === 'chat') return 'ask';
  if (m === 'control' || m === 'master' || m === 'employee') return m;
  return 'ask';
}

export function buildChatStateFromRedisRows(messages: ChatHistoryRedisRow[]) {
  // Assign clientOrder from chronology, not raw row order — history rows can come back
  // out of wall-clock order across turns. Sort only when every row parses to a real
  // timestamp; otherwise keep server order (conservative fallback).
  // Thai: เรียงตามเวลาก่อนแจก clientOrder เฉพาะเมื่อทุกแถวมี timestamp ที่ parse ได้
  const parsedTimes = messages.map((row) => parseChatTimestampMs(row.timestamp || row.created_at));
  const rows = parsedTimes.every(Number.isFinite)
    ? messages
        .map((row, index) => ({ row, t: parsedTimes[index] }))
        .sort((a, b) => a.t - b.t) // Array.sort is stable — equal timestamps keep server order
        .map((entry) => entry.row)
    : messages;
  let order = 0;
  let turn = 0;
  const um: UserMessage[] = [];
  const hm: ChatHistoryMessage[] = [];
  const singleReplyIndexByTurn = new Map<string, number>();

  const pushAgentMessage = (msg: ChatHistoryMessage) => {
    const msgType = msg.message_type || '';
    const isSingleReplyType =
      msgType === 'chat_response' ||
      msgType === 'employee_direct' ||
      msgType === 'manager_result' ||
      msgType === 'master_final_result';
    if (!isSingleReplyType) {
      hm.push(msg);
      return;
    }

    const key = `${turn}|${msg.agent_id}|${msgType}|${msg.mode || ''}`;
    const existingIndex = singleReplyIndexByTurn.get(key);
    if (existingIndex !== undefined) {
      const existing = hm[existingIndex];
      const existingTs = parseChatTimestampMs(existing.timestamp);
      const nextTs = parseChatTimestampMs(msg.timestamp);
      hm[existingIndex] =
        Number.isFinite(nextTs) && (!Number.isFinite(existingTs) || nextTs >= existingTs)
          ? msg
          : existing;
      return;
    }

    singleReplyIndexByTurn.set(key, hm.length);
    hm.push(msg);
  };

  for (const row of rows) {
    const rowTs = row.timestamp || row.created_at;
    if (typeof row.agent_id === 'string') {
      order += 1;
      const agentId = row.agent_id || 'system';
      const msgType = row.message_type || 'status';

      if (agentId === 'user') {
        turn += 1;
        um.push({
          text: row.content || '',
          time: Number.isFinite(parseChatTimestampMs(rowTs))
            ? parseChatTimestampMs(rowTs)
            : Date.now(),
          mode: redisRowModeToUserMode(row.mode),
          clientOrder: order,
        });
      } else {
        pushAgentMessage({
          agent_id: agentId,
          agent_name: row.agent_name || agentId,
          content: row.content || '',
          timestamp: rowTs || new Date().toISOString(),
          message_type: msgType,
          clientOrder: order,
          ...(row.mode ? { mode: row.mode } : {}),
        } as ChatHistoryMessage);
      }
      continue;
    }

    const role = typeof row.role === 'string' ? row.role.toLowerCase() : '';
    const isAssistant = role === 'assistant';
    const lane =
      typeof row.mode === 'string' &&
      ['chat', 'ask', 'insight', 'insights', 'control', 'master', 'employee'].includes(row.mode)
        ? row.mode
        : 'ask';
    if (isAssistant) {
      order += 1;
      if (lane === 'master') {
        pushAgentMessage({
          agent_id: 'master',
          agent_name: 'Master Agent',
          content: row.content || '',
          timestamp: rowTs || new Date().toISOString(),
          message_type: 'master_final_result',
          mode: lane,
          clientOrder: order,
        });
      } else if (lane === 'employee') {
        pushAgentMessage({
          agent_id: 'device',
          agent_name: 'Device agent',
          content: row.content || '',
          timestamp: rowTs || new Date().toISOString(),
          message_type: 'employee_direct',
          mode: lane,
          clientOrder: order,
        });
      } else if (lane === 'ask' || lane === 'insight' || lane === 'insights' || lane === 'control') {
        pushAgentMessage({
          agent_id: 'manager',
          agent_name: 'Station Manager',
          content: row.content || '',
          timestamp: rowTs || new Date().toISOString(),
          message_type: 'manager_result',
          mode: lane,
          clientOrder: order,
        });
      } else {
        pushAgentMessage({
          agent_id: 'assistant',
          agent_name: 'AgentStation Assistant',
          content: row.content || '',
          timestamp: rowTs || new Date().toISOString(),
          message_type: 'chat_response',
          mode: lane,
          clientOrder: order,
        });
      }
    } else {
      order += 1;
      turn += 1;
      um.push({
        text: row.content || '',
        time: Number.isFinite(parseChatTimestampMs(rowTs))
          ? parseChatTimestampMs(rowTs)
          : Date.now(),
        mode: redisRowModeToUserMode(row.mode),
        clientOrder: order,
      });
    }
  }
  return { userMessages: um, history: hm, nextOrder: order };
}

// ─── Merge helpers: prevent Redis-lag wipes of in-flight WS messages ─────────

/** Dedupe WS vs Redis copies: ignore timestamp (backend/JSON may reserialize differently). */
export function chatHistoryDedupeKey(m: ChatHistoryMessage): string {
  const c = m.content || '';
  let h = 5381;
  for (let i = 0; i < c.length; i++) {
    h = (h << 5) + h + c.charCodeAt(i);
  }
  return `${m.agent_id}|${m.message_type || ''}|${h >>> 0}|${c.length}|${c.slice(0, 96)}`;
}

/** @deprecated internal alias retained for existing call sites. */
const _historyKey = chatHistoryDedupeKey;

/**
 * Coarse identity used to recognize that a Redis row is the *final* version of a row we
 * previously appended from WS as a *streaming/partial* token. The dedupe key above uses a
 * hash of content, so a partial "Hello " and a final "Hello world" are different keys
 * and would otherwise both survive the merge → user sees a duplicate bubble ("stuck"
 * text). Match on agent_id + message_type alone (within the same turn), preferring the
 * row whose content is a prefix of the other (final ≥ partial).
 */
function sameAgentTypePrefix(a: ChatHistoryMessage, b: ChatHistoryMessage): boolean {
  if (a.agent_id !== b.agent_id) return false;
  if ((a.message_type || '') !== (b.message_type || '')) return false;
  const ac = a.content || '';
  const bc = b.content || '';
  return ac.startsWith(bc) || bc.startsWith(ac);
}

export function mergeChatHistory(
  local: ChatHistoryMessage[],
  redis: ChatHistoryMessage[],
): ChatHistoryMessage[] {
  if (redis.length === 0) return local;
  if (local.length === 0) return redis;
  const seen = new Set(redis.map(_historyKey));
  const merged = [...redis];
  for (const m of local) {
    const key = _historyKey(m);
    if (seen.has(key)) {
      // Same content on both sides — keep the Redis copy as-is. Redis rows carry the
      // canonical chronology-derived order for the whole conversation, so adopting it
      // lets a re-sync heal stream-order inversions (a reply appended after the next
      // question because its WS frame arrived late). The previous Math.max ratchet
      // froze those inversions permanently.
      // Thai: ใช้ลำดับจากฝั่ง Redis เพื่อให้การ sync ซ่อมลำดับที่สลับได้
      continue;
    }
    // Drop a stale WS row whose final version already arrived in Redis (avoids
    // duplicate "stuck" bubbles where partial streaming tokens persist next to the
    // finalized message after a Redis re-sync).
    const supersededByRedis = merged.some((r) => sameAgentTypePrefix(r, m));
    if (supersededByRedis) continue;
    merged.push(m);
  }
  merged.sort((a, b) =>
    compareChatDisplayOrder(
      { clientOrder: a.clientOrder, sortTimeMs: parseChatTimestampMs(a.timestamp) },
      { clientOrder: b.clientOrder, sortTimeMs: parseChatTimestampMs(b.timestamp) },
    ),
  );
  return merged;
}

function latestCachedTranscriptMs(cached: {
  history: ChatHistoryMessage[];
  userMessages: UserMessage[];
}): number {
  const times = [
    ...cached.history.map((m) => parseChatTimestampMs(m.timestamp)),
    ...cached.userMessages.map((m) => m.time),
  ].filter(Number.isFinite);
  return times.length ? Math.max(...times) : -1;
}

function latestRedisTranscriptMs(
  userMessages: UserMessage[],
  history: ChatHistoryMessage[],
): number {
  const times = [
    ...history.map((m) => parseChatTimestampMs(m.timestamp)),
    ...userMessages.map((m) => m.time),
  ].filter(Number.isFinite);
  return times.length ? Math.max(...times) : -1;
}

function _isSameUserMessage(a: UserMessage, b: UserMessage): boolean {
  if (a.text !== b.text) return false;
  if (a.chatTarget && b.chatTarget && a.chatTarget !== b.chatTarget) return false;
  // Use a 5-minute window to account for long-running tasks where the server
  // saves the timestamp at the end of the task.
  return Math.abs(a.time - b.time) <= 300_000;
}

export function mergeUserMessages(local: UserMessage[], redis: UserMessage[]): UserMessage[] {
  if (redis.length === 0) return local;
  if (local.length === 0) return redis;
  const merged = [...redis];
  const matchedRedisIndexes = new Set<number>();
  for (const m of local) {
    const existingIndex = merged.findIndex(
      (existing, index) => !matchedRedisIndexes.has(index) && _isSameUserMessage(existing, m),
    );
    if (existingIndex >= 0) {
      matchedRedisIndexes.add(existingIndex);
      merged[existingIndex] = {
        ...merged[existingIndex],
        // Preserve chatTarget from the local message — Redis rows never carry it,
        // so without this the device-direct lane filter loses the device association.
        ...(m.chatTarget !== undefined ? { chatTarget: m.chatTarget } : {}),
      };
      continue;
    }
    merged.push(m);
  }
  merged.sort((a, b) =>
    compareChatDisplayOrder(
      { clientOrder: a.clientOrder, sortTimeMs: a.time },
      { clientOrder: b.clientOrder, sortTimeMs: b.time },
    ),
  );
  return merged;
}
